import axios from 'axios';
import React, { useState, useEffect } from 'react';
import { 
  FaFacebook, FaTwitter, FaInstagram, FaLinkedin, 
  FaYoutube, FaTiktok, FaPinterest
} from 'react-icons/fa';
import Swal from 'sweetalert2';
const SocialConnectModal = ({ show, onClose, onConnect, platform }) => {
  const [authData, setAuthData] = useState({
    username: '',
    accessToken: '',
    refreshToken: ''
  });
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [userId, setUserId] = useState(null);

  // Platform configurations
  const platformConfigs = {
    facebook: {
      name: 'Facebook',
      icon: <FaFacebook className="text-blue-600 text-2xl" />,
      description: 'Connect your Facebook Pages and Instagram accounts'
    },
    instagram: {
      name: 'Instagram',
      icon: <FaInstagram className="text-pink-600 text-2xl" />,
      description: 'Connect your Instagram Business account'
    }
  };

  // Get user ID from localStorage
  useEffect(() => {
    const storedUserId = localStorage.getItem("userId");
    if (storedUserId) {
      setUserId(storedUserId);
    } else {
      setUserId('1');
    }
  }, []);
const generateState = () => {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};
  // OAuth handler
const handleOAuth = async () => {
  if (!platform) {
    showErrorAlert('Platform not specified');
    return;
  }

  try {
    setIsAuthenticating(true);

    console.log('Starting OAuth for platform:', platform);

    // Generate secure state

const state = generateState(); // or generateSimpleState()
    console.log('Generated state:', state);
    // Store state in multiple locations for redundancy
    localStorage.setItem('oauth_state', state);
    localStorage.setItem('oauth_platform', platform);
    localStorage.setItem('oauth_timestamp', Date.now().toString());
    
    // Also store in sessionStorage as backup
    sessionStorage.setItem('oauth_state', state);
    sessionStorage.setItem('oauth_platform', platform);

    // Get OAuth URL from backend
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://backend.atocreative.et';
    const response = await axios.get(`${backendUrl}/api/auth/${platform}/oauth-url`, {
      params: { 
        userId: userId?.toString() || '1',
        state: state // Send the generated state to backend
      }
    });

    console.log('Received OAuth URL from backend');

    if (!response.data.authUrl) {
      throw new Error('No auth URL received from server');
    }

    // Use the backend's state if provided, otherwise use our generated one
    const finalState = response.data.state || state;
    if (response.data.state && response.data.state !== state) {
      console.log('Using backend-generated state:', response.data.state);
      localStorage.setItem('oauth_state', response.data.state);
      sessionStorage.setItem('oauth_state', response.data.state);
    }

    console.log('Final state being used:', finalState);
    console.log('Redirecting to OAuth URL...');

    // Redirect to OAuth URL in same window
    window.location.href = response.data.authUrl;

  } catch (error) {
    console.error('OAuth initiation failed:', error);
    let errorMessage = 'Failed to start authentication. Please try again.';
    
    if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    showErrorAlert(errorMessage);
    setIsAuthenticating(false);
    
    // Clean up on error
    localStorage.removeItem('oauth_state');
    localStorage.removeItem('oauth_platform');
    localStorage.removeItem('oauth_timestamp');
    sessionStorage.removeItem('oauth_state');
    sessionStorage.removeItem('oauth_platform');
  }
};
  const handleConnectionSuccess = async (accountData) => {
    try {
      // Update localStorage for immediate UI update
      const socialAccounts = JSON.parse(localStorage.getItem("socialAccounts")) || [];
      const updatedAccounts = [
        ...socialAccounts.filter((a) => a.platform !== platform),
        { 
          ...accountData, 
          user_id: userId, 
          connected_at: new Date().toISOString()
        }
      ];
      localStorage.setItem("socialAccounts", JSON.stringify(updatedAccounts));

      showSuccessAlert(`${platformConfigs[platform]?.name || platform} account connected successfully!`);
      
      setIsAuthenticating(false);
      
      // Update parent component
      onConnect(accountData);
      onClose();
      
    } catch (error) {
      console.error('Error in connection success:', error);
      showErrorAlert('Account connected but failed to update local storage.');
    }
  };

  const handleManualConnect = async () => {
    if (!authData.username || !authData.accessToken) {
      showErrorAlert('Please fill in username and access token');
      return;
    }

    try {
      setIsAuthenticating(true);
      
      // Create mock account data for manual connection
      const mockAccountData = {
        username: authData.username,
        account_id: `manual_${Date.now()}`,
        access_token: authData.accessToken,
        refresh_token: authData.refreshToken || '',
        platform: platform,
        connected_at: new Date().toISOString(),
        profile_picture: null,
        email: `${authData.username}@${platform}.com`,
        name: authData.username,
        pages: []
      };

      await handleConnectionSuccess(mockAccountData);
      
    } catch (error) {
      console.error('Manual connection failed:', error);
      showErrorAlert('Failed to connect account. Please check your credentials.');
      setIsAuthenticating(false);
    }
  };

  // Alert helpers
  const showSuccessAlert = (message) => {
    Swal.fire({
      title: 'Success!',
      text: message,
      icon: 'success',
      confirmButtonColor: '#FD5F20',
    });
  };

  const showErrorAlert = (message) => {
    Swal.fire({
      title: 'Error!',
      text: message,
      icon: 'error',
      confirmButtonColor: '#FD5F20',
    });
  };

  if (!show) return null;

  const config = platformConfigs[platform] || {
    name: platform,
    icon: <div className="text-gray-600 text-2xl">🔗</div>,
    description: `Connect your ${platform} account`
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-96 max-h-full overflow-y-auto">
        <h3 className="text-xl font-bold mb-4">Connect {config.name} Account</h3>
        
        <div className="flex items-center mb-4">
          <span className="mr-2">{config.icon}</span>
          <span className="font-semibold">{config.name}</span>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          {config.description}
        </p>

        {isAuthenticating ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FD5F20] mx-auto"></div>
            <p className="mt-4 text-gray-600">Connecting to {config.name}...</p>
            <p className="text-sm text-gray-500 mt-2">You will be redirected to {config.name} for authentication</p>
          </div>
        ) : (
          <div className="space-y-4">
            <button
              onClick={handleOAuth}
              className="w-full bg-[#FD5F20] text-white py-3 rounded-lg hover:bg-[#e55a1d] transition-colors flex items-center justify-center"
            >
              Connect with {config.name}
            </button>

            <div className="relative flex items-center">
              <div className="flex-grow border-t border-gray-300"></div>
              <span className="mx-4 text-gray-500 text-sm">or connect manually</span>
              <div className="flex-grow border-t border-gray-300"></div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username
              </label>
              <input
                type="text"
                value={authData.username}
                onChange={(e) => setAuthData(prev => ({...prev, username: e.target.value}))}
                className="w-full border rounded-lg p-2 text-sm focus:border-[#FD5F20] focus:ring-1 focus:ring-[#FD5F20]"
                placeholder={`Enter your ${config.name} username`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Access Token *
              </label>
              <input
                type="text"
                value={authData.accessToken}
                onChange={(e) => setAuthData(prev => ({...prev, accessToken: e.target.value}))}
                className="w-full border rounded-lg p-2 text-sm focus:border-[#FD5F20] focus:ring-1 focus:ring-[#FD5F20]"
                placeholder="Paste access token here"
              />
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button 
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleManualConnect}
                className="px-4 py-2 bg-[#FD5F20] text-white rounded-lg hover:bg-[#e55a1d] transition-colors"
                disabled={!authData.accessToken}
              >
                Connect Account
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialConnectModal;